import React from 'react';
import mapValues from 'lodash/mapValues';
import classnames from 'classnames';
import sanitizeHtml from 'sanitize-html';

import { makeStyles } from '@material-ui/core/styles';

import { useI18n } from '@hydra/common/customHooks';

import logger from '@hydra/utils/logger';
import settings from '@hydra/utils/settings';

const useStyles = makeStyles({
  translationMissing: { display: 'none' }
});

export function escapeInterpolations (unsanitizedWith?: { [key: string]: string | number }) {
  if (!unsanitizedWith) {
    return {};
  }

  const { allowedTags, allowedAttributes } = settings.get('translations');

  const cleanWith = mapValues(unsanitizedWith, withValue => {
    return (typeof withValue === 'string')
      ? sanitizeHtml(withValue, { allowedTags, allowedAttributes })
      : withValue;
  });

  return cleanWith;
}

export function getTranslationError (err: Error, content: string) {
  logger.error({
    event: 'translation-error',
    err,
    content
  });

  return '[translation error]';
}

export function getTranslation ({
  translateFn,
  content,
  allowLinks
}: {
  translateFn: (tag: string) => string | null
  content: string
  allowLinks: boolean
}): string | null {
  try {
    const translated = translateFn(content);

    if (!translated) {
      return translated;
    }

    if (allowLinks) {
      return translated;
    }
    return sanitizeHtml(translated, {
      transformTags: {
        a: () => ({
          tagName: 'span',
          attribs: {}
        })
      },
      allowedTags: false
    });
  } catch (err) {
    return getTranslationError(err, content);
  }
}

export function concatenateStringsWithComponents (content: (string | React.ReactElement<unknown, string | React.JSXElementConstructor<any>>)[]) {
  if (!Array.isArray(content)) {
    return [];
  }
  return content.reduce((result, piece) => {
    if (React.isValidElement(piece)) {
      return result.concat(piece);
    } else {
      const previous = result[result.length - 1] || '';
      const nextPieces = React.isValidElement(previous)
        ? result.concat(piece)
        : result.slice(0, -1).concat(previous + piece);

      return nextPieces;
    }
  }, [] as (string | React.ReactElement<unknown, string | React.JSXElementConstructor<any>>)[]);
}

// We map the interpolations to an array of
// * non-interpolated text
// * resolved interpolated values
// * (possible) valid react components
// * (possible) translation errors
export function doInterpolations (interpolations: { [key: string]: string | number }, translationKey: string) {
  return function (piece: string, index: number) {
    if (index % 2 === 0) {
      return piece;
    } else {
      const match = interpolations[piece];
      if (React.isValidElement(match)) {
        return React.cloneElement(match, { key: `${index}-element` });
      } else if (typeof match === 'string' || typeof match === 'number') {
        return match.toString();
      } else if (typeof match === 'undefined') {
        const err = Object.assign(
          new Error(`Substitution value "${piece}" is not defined for "${translationKey}"`),
          { translationKey, missingSubstitution: piece }
        );
        return getTranslationError(err, piece);
      } else {
        const err = Object.assign(new Error('Invalid match type'), { match });
        return getTranslationError(err, piece);
      }
    }
  };
}

interface InterpolateProps {
  className?: string
  component?: string
  content: string
  with?: { [key: string]: string | number }
  allowLinks?: boolean
}

export function Interpolate (props: InterpolateProps) {
  const {
    allowLinks = true,
    component = 'span',
    content,
    className: classNameProp
  } = props;
  const { translate } = useI18n();
  const classes = useStyles();

  const interpolate = (translation: string, interpolations: { [key: string]: string | number }) => {
    const REGEXP = /%\((.+?)\)s/;

    const interpolatedContent = translation
      .split(REGEXP)
      .map(doInterpolations(interpolations, content))
      .filter((content: string| React.ReactElement<unknown, string | React.JSXElementConstructor<any>>) => !!content);

    return interpolatedContent.some(React.isValidElement)
      ? { children: concatenateStringsWithComponents(interpolatedContent) }
      : { dangerouslySetInnerHTML: { __html: interpolatedContent.join('') } };
  };

  const interpolations = escapeInterpolations(props.with);
  const rawTranslation = getTranslation({
    translateFn: translate,
    content,
    allowLinks
  });

  const interpolatedContent = !rawTranslation || rawTranslation.startsWith('missing translation: ')
    ? {}
    : interpolate(rawTranslation, interpolations);

  const className = classnames(
    (!rawTranslation || rawTranslation.startsWith('missing translation: ')) && classes.translationMissing,
    classNameProp
  );

  const finalProps = {
    ...interpolatedContent,
    className
  };

  return React.createElement(component, finalProps);
}

export interface TranslateProps {
  allowLinks?: boolean
  className?: string
  component?: string
  content: string
  with?: { [key: string]: string | number }
}

export function Translate (props: TranslateProps) {
  const {
    allowLinks = true,
    component = 'span',
    content,
    className: classNameProp
  } = props;
  const { translate } = useI18n();
  const classes = useStyles();

  if (props.with && Object.keys(props.with).length > 0) {
    return <Interpolate {...props} />;
  }

  const translation = getTranslation({
    translateFn: translate,
    content,
    allowLinks
  });

  const translationMissing = !translation || translation.startsWith('missing translation: ');

  const className = classnames(
    translationMissing && classes.translationMissing,
    classNameProp
  );

  // Strings coming out of our translator platform are trusted
  const finalProps = { dangerouslySetInnerHTML: { __html: translation }, className };

  return React.createElement(component, finalProps);
}

export default Translate;
